
import { get, map, set } from 'lodash';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import moment from 'moment';

function exportToExcel(jsonData, fileName) {
  const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';
  const ws = XLSX.utils.json_to_sheet(jsonData);
  const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], {type: fileType});
  FileSaver.saveAs(data, fileName + fileExtension);
}

function exportToExcelWithSheets(jsonDataArray, sheetNames, fileName) {
  const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';
  const sheets = {};
  jsonDataArray.forEach((jsonData, index) => {
    sheets[sheetNames[index]] = XLSX.utils.json_to_sheet(jsonData);
  });
  const wb = { Sheets: sheets, SheetNames: sheetNames };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], {type: fileType});
  FileSaver.saveAs(data, fileName + fileExtension);
}

function getServiceExcelData(repoName, services, subscriptionsInfoStatistics, subscriptionInfoDate) {
  const validStatuses = ['REQ', 'ACT', 'INA'];
  const activatedStatuses = ['ACT'];

  const generalStats = {};
  const names = {};

  services.forEach(service => {
    generalStats[service] = {};
    names[service] = [];
    map(subscriptionsInfoStatistics, (stat, name) => {
      if (stat.generalInfo && stat.generalInfo.clientRepoName === repoName) {
        const softwareServiceRecords = get(stat.services, service, false);
        if (softwareServiceRecords) {
          softwareServiceRecords.forEach((record, i) => {
            const startDate = get(record, 'startDate', null);
            const endDate = get(record, 'endDate', null);

            const hasOne = (softwareServiceRecords.length === 1);
            const isFirst = (i === 0);
            const isLast = (i === (softwareServiceRecords.length - 1));

            let startStatus = '';
            let endStatus = '';

            if (hasOne) {
              if (startDate && !endDate) {
                startStatus = 'INA';
                endStatus = 'ACT';
              } else if (!startDate && endDate) {
                startStatus = 'INVALID';
                endStatus = 'INVALID';
              } else if (startDate && endDate) {
                const isStartDateBefore = moment(startDate) < moment(subscriptionInfoDate.startDate);
                const isEndDateAfter = moment(endDate) > moment(subscriptionInfoDate.endDate);
                startStatus = isStartDateBefore ? 'ACT' : 'INA';
                endStatus = isEndDateAfter ? 'ACT' : 'INA';
              } else if (!startDate && !endDate) {
                startStatus = record.status;
                endStatus = record.status;
              }
            }
            else {
              if (startDate && !endDate) {
                startStatus = 'NONE';
                endStatus = 'ACT';
              } else if (!startDate && endDate) {
                startStatus = 'INVALID';
                endStatus = 'INVALID';
              } else if (startDate && endDate) {
                if (isFirst) {
                  startStatus = 'INA';
                  endStatus = 'NONE';
                }
                else if (isLast) {
                  startStatus = 'NONE';
                  endStatus = 'INA';
                }
                else {
                  startStatus = 'NONE';
                  endStatus = 'NONE';
                }
              } else if (!startDate && !endDate) {
                startStatus = 'INVALID';
                endStatus = 'INVALID';
              }
            }
            if (validStatuses.includes(startStatus)) {
              set(
                  generalStats,
                  [service, name, 'startStatus'],
                  startStatus,
              );
            }
            if (validStatuses.includes(endStatus)) {
              set(
                  generalStats,
                  [service, name, 'endStatus'],
                  endStatus,
              );
            }

            let excelStartDate = startDate;
            let excelEndDate = endDate;

            if (!excelStartDate && activatedStatuses.includes(startStatus)) {
              excelStartDate = subscriptionInfoDate.startDate;
            }
            if (!excelEndDate && activatedStatuses.includes(endStatus)) {
              excelEndDate = subscriptionInfoDate.endDate;
            }

            excelStartDate = excelStartDate ? moment(excelStartDate) : null;
            excelEndDate = excelEndDate ? moment(excelEndDate) : null;

            excelStartDate = excelStartDate ? excelStartDate.format('YYYY-MM-DD HH:mm:ss') : null;
            excelEndDate = excelEndDate ? excelEndDate.format('YYYY-MM-DD HH:mm:ss') : null;

            names[service].push([name, stat.generalInfo.userEmail, excelStartDate, excelEndDate, startStatus, endStatus]);
          });
        }
      }
    });
  });

  return {generalStats, names};
}

function getServiceGeneralData(generalStats) {
  const activatedStatuses = ['ACT'];
  const deactivatedStatuses = ['REQ', 'INA'];
  const serviceGeneralData = {};
  map(generalStats, (name, softwareService) => {
    let numberOfUsersFirstDate = 0;
    let numberOfUsersSecondDate = 0;
    let numberOfNewUsers = 0;
    let numberOfDeactivatedUsers = 0;
    map(name, (statuses, nameKey) => {
      if (statuses.startStatus === 'ACT') {
        numberOfUsersFirstDate += 1;
      }
      if (statuses.endStatus === 'ACT') {
        numberOfUsersSecondDate += 1;
      }
      if (deactivatedStatuses.includes(statuses.startStatus) && activatedStatuses.includes(statuses.endStatus)) {
        numberOfNewUsers += 1;
      }
      if (activatedStatuses.includes(statuses.startStatus) && deactivatedStatuses.includes(statuses.endStatus)) {
        numberOfDeactivatedUsers += 1;
      }
    });
    serviceGeneralData[softwareService] = {
      numberOfUsersFirstDate,
      numberOfUsersSecondDate,
      numberOfNewUsers,
      numberOfDeactivatedUsers,
    }
  });
  return serviceGeneralData;
}

function exportToExcelGetSubscriptionsInfo(selectedClients, subscriptionInfoDate, subscriptionsInfoStatistics, services, fileName){
  const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';
  const sheets = {};
  
  selectedClients.forEach(client => {
    sheets[client.name] = XLSX.utils.aoa_to_sheet([['Time Period - Start Date', 'Time Period - End Date', 'Repository Name']], {origin: 'A1'});
    XLSX.utils.sheet_add_aoa(sheets[client.name], [[subscriptionInfoDate.startDate, subscriptionInfoDate.endDate, client.name]], {origin: 'A2'});
  
    XLSX.utils.sheet_add_aoa(sheets[client.name], [[
      'Service',
      'Number of users - first date',
      'Number of users - second date',
      'New Users',
      'Deactivated Users'
    ]], {origin: 'A4'});

    const firstServiceBeginningNum = 6 + services.length;
    XLSX.utils.sheet_add_aoa(sheets[client.name], [['Software Service Subscriptions']], {origin: `A${firstServiceBeginningNum}`});
    const softwareServiceHeaders = [[
      'User',
      'User Email',
      'Start of Subscription',
      'End of Subscription',
      'Status at the beginning of period',
      'Status at the end of period',
    ]];
    const { names, generalStats } = getServiceExcelData(client.name, services, subscriptionsInfoStatistics, subscriptionInfoDate);

    let softwareServiceIndex = 0;
    let serviceBeginningNum = firstServiceBeginningNum + 1;

    services.forEach(service => {
      const {
        numberOfUsersFirstDate,
        numberOfUsersSecondDate,
        numberOfNewUsers,
        numberOfDeactivatedUsers,
      } = getServiceGeneralData(generalStats)[service];
      XLSX.utils.sheet_add_aoa(sheets[client.name], [[
        service,
        numberOfUsersFirstDate,
        numberOfUsersSecondDate,
        numberOfNewUsers,
        numberOfDeactivatedUsers,
      ]], {origin: `A${5+softwareServiceIndex}`});
      softwareServiceIndex += 1;
      XLSX.utils.sheet_add_aoa(sheets[client.name], [[service]], {origin: `A${serviceBeginningNum}`});
      XLSX.utils.sheet_add_aoa(sheets[client.name], softwareServiceHeaders, {origin: `A${serviceBeginningNum+1}`});
      XLSX.utils.sheet_add_aoa(sheets[client.name], names[service], {origin: `A${serviceBeginningNum+2}`});
      serviceBeginningNum += names[service].length + 4;
    });
  });

  const wb = { Sheets: sheets, SheetNames: selectedClients.map(client => client.name) };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], {type: fileType});
  FileSaver.saveAs(data, fileName + fileExtension);
}

function exportToExcelGetSubscriptionsInfoPerUser(
  selectedClients,
  clientData,
  trtClients,
  billableInfo,
  priceInfo,
  subscriptionInfoDate,
  subscriptionsInfoStatistics,
  services,
  fileName,
) {
  const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';
  const sheets = {};
  const USERS_SHEET_NAME = 'Subscriptions';
  const WORKING_PAPERS_SHEET_NAME = 'Working Papers';

  sheets[USERS_SHEET_NAME] = XLSX.utils.aoa_to_sheet(
    [[
      'Client Name',
      'Repository Name',
      'Address',
      'City',
      'E-Mail',
      'VAT',
      'Country Code',
      'Country Name',
      'User',
      'User Email',
      'Start of Subscription (TAS)',
      'End of Subscription (TAS)',
      'Status at the beginning of period (TAS)',
      'Status at the end of period (TAS)',
      'Working Papers (yes / no)',
      'TRT Numer of imports',
      'Current Payments Model',
      'Last Transition From/To Billable',
      'Price TAS',
      'Currency Code TAS',
      'Currency Name TAS',
      'Discount TAS',
      'Price Note TAS',
      'Note TAS',
      'Price TRT',
      'Currency Code TRT',
      'Currency Name TRT',
      'Discount TRT',
      'Price Note TRT',
      'Note TRT',
      'Log Included',
      'Log Price',
      'Log Currency Code',
      'Log Currency Name',
      'Log Discount',
      'Log Note',
    ]], { origin: 'A1' }
  );

  sheets[WORKING_PAPERS_SHEET_NAME] = XLSX.utils.aoa_to_sheet(
    [[
      'Client Name',
      'Repository Name',
      'Working Paper',
      'Start of Subscription',
      'End of Subscription',
      'Status at the beginning of period',
      'Status at the end of period',
    ]], { origin: 'A1' }
  );

  let usersSheetRowIndex = 0;
  let workingPapersSheetRowIndex = 0;

  selectedClients.forEach((client) => {
    const NOT_FOUND = '/';

    const repoName = client.name;
    const clientInfo = clientData.find(
      clientRow => clientRow?.repoName === repoName,
    ) || {
      name: NOT_FOUND,
      countryName: NOT_FOUND,
      countryCode: NOT_FOUND,
    };

    const { names, generalStats } = getServiceExcelData(
      repoName,
      services,
      subscriptionsInfoStatistics,
      subscriptionInfoDate,
    );

    const hasWorkingPapers = Object.keys(generalStats).some(
      (key) =>
        key.toLowerCase().includes('paper') &&
        Object.keys(generalStats[key]).some(
          (statsRepoName) =>
            statsRepoName === repoName &&
              generalStats?.[key]?.[statsRepoName]?.endStatus === 'ACT',
        ),
    );

    const billableInfoForRepo = billableInfo.find(
      billableRow => billableRow.repo_name === repoName,
    );

    // eslint-disable-next-line camelcase
    const currentPaymentsModel = billableInfoForRepo?.payments_model || NOT_FOUND;
    // eslint-disable-next-line camelcase
    const lastBillableTransition = billableInfoForRepo?.last_billable_transition;

    const lastBillableTransitionFormatted =
      lastBillableTransition ?
        moment(lastBillableTransition).format('YYYY-MM-DD HH:mm:ss') :
        NOT_FOUND;

    const priceTAS = priceInfo.find(
      priceRow =>
        priceRow.client__repo__name === repoName &&
        priceRow.service_price__service__software__name === 'Tickmark audit software',
    ); 

    const priceTRT = priceInfo.find(
      priceRow =>
        priceRow.client__repo__name === repoName &&
        priceRow.service_price__service__software__name === 'Tickmark reporting tool',
    );

    const notFoundPrice = {
      service_price__price: NOT_FOUND,
      service_price__currency__code: NOT_FOUND,
      service_price__currency__name: NOT_FOUND,
      service_price__discount: NOT_FOUND,
      service_price__note: NOT_FOUND,
      note: NOT_FOUND,
    };

    const getPriceData = (priceRow) => {
      if (!priceRow) {
        return Object.values(notFoundPrice);
      }

      return [
        priceRow.service_price__price,
        priceRow.service_price__currency__code,
        priceRow.service_price__currency__name,
        priceRow.service_price__discount,
        priceRow.service_price__note,
        priceRow.note,
      ];
    }

    const notFoundLog = {
      log_included: NOT_FOUND,
      log_price__price: NOT_FOUND,
      log_price__currency__code: NOT_FOUND,
      log_price__currency__name: NOT_FOUND,
      log_price__discount: NOT_FOUND,
      log_note: NOT_FOUND,
    };

    const getLogData = (priceRow) => {
      if (!priceRow) {
        return Object.values(notFoundLog);
      }

      return [
        priceRow.log_included,
        priceRow.log_price__price,
        priceRow.log_price__currency__code,
        priceRow.log_price__currency__name,
        priceRow.log_price__discount,
        priceRow.log_note,
      ];
    }

    const subscriptionInterval = names['Tickmark audit software'] || [];
    subscriptionInterval.forEach(subscription => {
      const trtImports = trtClients[repoName]?.find(
        trtUser => trtUser?.username === subscription[0],
      );

      XLSX.utils.sheet_add_aoa(
        sheets[USERS_SHEET_NAME],
        [[
          clientInfo.name,
          repoName,
          clientInfo.address,
          clientInfo.city,
          clientInfo.email,
          clientInfo.vat,
          clientInfo.countryCode,
          clientInfo.countryName,
          // User Info
          ...subscription || [
            NOT_FOUND,
            NOT_FOUND,
            NOT_FOUND,
            NOT_FOUND,
            NOT_FOUND,
            NOT_FOUND,
          ],
          hasWorkingPapers ? 'yes' : 'no',
          trtImports?.count || NOT_FOUND,
          currentPaymentsModel,
          lastBillableTransitionFormatted,
          ...getPriceData(priceTAS),
          ...getPriceData(priceTRT),
          ...getLogData(priceTAS),
        ]],
        { origin: `A${usersSheetRowIndex + 2}` },
      );

      usersSheetRowIndex += 1;
    });

    Object.keys(names).filter((key) => key.toLowerCase().includes('paper')).forEach(
      (key) => {
        const workingPaperSubscriptionInterval = names[key];
        workingPaperSubscriptionInterval.forEach(subscription => {
          XLSX.utils.sheet_add_aoa(
            sheets[WORKING_PAPERS_SHEET_NAME],
            [[
              clientInfo.name,
              repoName,
              key,
              ...subscription.slice(2) || [
                NOT_FOUND,
                NOT_FOUND,
                NOT_FOUND,
                NOT_FOUND,
              ],
            ]],
            { origin: `A${workingPapersSheetRowIndex + 2}` },
          );

          workingPapersSheetRowIndex += 1;
        });
      }
    )
  });

  const wb = { Sheets: sheets, SheetNames: [USERS_SHEET_NAME, WORKING_PAPERS_SHEET_NAME] };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], {type: fileType});
  FileSaver.saveAs(data, fileName + fileExtension); 
}

export default {
  exportToExcel,
  exportToExcelWithSheets,
  exportToExcelGetSubscriptionsInfo,
  exportToExcelGetSubscriptionsInfoPerUser,
};